import React from 'react';
import { formatINR } from '../billingUtils';
import './InvoiceTotalsPanel.css';

export const trimNumber = (value) => String(value ?? '').replace(/\.0+$|(\.\d*?)0+$/, '$1');

const signed = (value) => {
  const n = Number(value || 0);
  return `${n < 0 ? '−' : '+'}${formatINR(Math.abs(n))}`;
};

/**
 * Proforma totals exactly as the server computed them (from `calculate` or
 * a saved PI). Nothing is added up here.
 */
const ProformaTotalsPanel = ({ totals, unit = 'MW', loading = false, error = '', placeholder = 'Select a client to see the totals.' }) => {
  if (!totals) {
    return (
      <div className="itp">
        <div className="itp-placeholder">{error || placeholder}</div>
      </div>
    );
  }

  const rate = trimNumber(totals.gstRate);
  const half = trimNumber(totals.halfRate) || (rate ? String(Number(rate) / 2) : '');
  const hasAdvance = totals.advancePercent != null;

  const taxRows = (prefix, cgst, sgst, igst) => (
    <>
      {totals.taxType === 'IGST' && (
        <div className="itp-row">
          <dt>{prefix}IGST @ {rate}%</dt>
          <dd>{formatINR(igst)}</dd>
        </div>
      )}
      {totals.taxType === 'CGST_SGST' && (
        <>
          <div className="itp-row">
            <dt>{prefix}CGST @ {half}%</dt>
            <dd>{formatINR(cgst)}</dd>
          </div>
          <div className="itp-row">
            <dt>{prefix}SGST @ {half}%</dt>
            <dd>{formatINR(sgst)}</dd>
          </div>
        </>
      )}
    </>
  );

  return (
    <div className={`itp ${loading ? 'is-loading' : ''}`} aria-busy={loading}>
      <dl className="itp-rows">
        <div className="itp-row">
          <dt>Total quantity</dt>
          <dd>{trimNumber(totals.totalQuantity) || '0'} {unit}</dd>
        </div>
        <div className="itp-row">
          <dt>Taxable amount</dt>
          <dd>{formatINR(totals.taxableAmount)}</dd>
        </div>

        {taxRows('', totals.cgstAmount, totals.sgstAmount, totals.igstAmount)}

        {!totals.taxType && (
          <div className="itp-row itp-warn">
            <dt>Tax</dt>
            <dd>Not applied — client state unknown</dd>
          </div>
        )}

        <div className="itp-row">
          <dt>Round off</dt>
          <dd>{signed(totals.roundOff)}</dd>
        </div>
        <div className="itp-row itp-total">
          <dt>Total</dt>
          <dd>{formatINR(totals.totalAmount)}</dd>
        </div>

        {hasAdvance ? (
          <>
            <div className="itp-row">
              <dt>Advance @ {trimNumber(totals.advancePercent)}%</dt>
              <dd>{formatINR(totals.advanceAmount)}</dd>
            </div>
            {taxRows('+ ', totals.advanceCgstAmount, totals.advanceSgstAmount, totals.advanceIgstAmount)}
            <div className="itp-row itp-balance">
              <dt>Advance payable</dt>
              <dd>{formatINR(totals.advanceTotal)}</dd>
            </div>
          </>
        ) : (
          <div className="itp-row itp-balance">
            <dt>Advance</dt>
            <dd>None requested</dd>
          </div>
        )}
      </dl>

      {totals.amountInWords && <p className="itp-words">{totals.amountInWords}</p>}
      {error && <p className="itp-error">{error}</p>}
    </div>
  );
};

export default ProformaTotalsPanel;
